import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions'; // o3-portalproj-9c4e7b21
import logo from '../assets/logo.jpg';

// --- IMPORTS ---
import PortalLayout from '../components/portal/PortalLayout'; 
import PortalLogin from './PortalLogin';         
import PortalQuotes from './PortalQuotes';       
import PortalQuoteDetail from './PortalQuoteDetail'; 
import PortalInvoices from './PortalInvoices';
import PortalAppointments from './PortalAppointments';
import PortalAppointmentDetail from './PortalAppointmentDetail'; // PHASE 1.5 VERIFIER: r1-cp-import-3f9a7c12
import PortalRequests from './PortalRequests';
import PortalNewRequest from './PortalNewRequest';
import PortalContact from './PortalContact'; 

// x7portal-6e1a3d58 — X7 (security audit 2026-10-15 #10): a portal sign-in lasts 30 days (claim
// portalExp, seconds). A session from before X7 has none and ends at 2026-11-16 00:00 UTC.
// MIRROR of LEGACY_PORTAL_SESSION_END in functions/utils/portalAudit.js and the default in
// firestore.rules isPortalUser(). The server and the rules refuse an ended session either way;
// this only makes the page say so instead of showing empty lists.
const LEGACY_PORTAL_SESSION_END = 1794787200;
const portalSessionEnded = (claims) => {
    if (!claims || claims.portalAccess !== true) return false;
    const end = typeof claims.portalExp === 'number' && claims.portalExp > 0 ? claims.portalExp : LEGACY_PORTAL_SESSION_END;
    return Date.now() / 1000 >= end;
};

export default function CustomerPortal() {
    const [user, setUser] = useState(null);
    const [organization, setOrganization] = useState(null); // New State
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // 1. Listen for Authentication Status & Fetch Org Data
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    // Confirm this is a PORTAL user and get Org ID
                    const idTokenResult = await currentUser.getIdTokenResult();
                    const orgId = idTokenResult.claims.organizationId;
                    // x7portal-6e1a3d58 — an ended session: sign out and say so.
                    if (portalSessionEnded(idTokenResult.claims)) {
                        await signOut(auth);
                        navigate('/portal/login?ended=1', { replace: true });
                        return;
                    }

                    // o3-portalproj-9c4e7b21 — was a whole-document read of
                    // organizations/{orgId}, which shipped QuickBooks tokens, Stripe
                    // ids, email templates and payroll settings to the customer's
                    // browser for the two fields PortalLayout actually renders.
                    // The server projection returns only public fields.
                    if (orgId) {
                        const getOrgInfo = httpsCallable(functions, 'getPublicOrganizationInfo');
                        const orgResult = await getOrgInfo();
                        setOrganization({ id: orgId, ...(orgResult.data || {}) });
                    }
                    
                    setUser(currentUser);
                } catch (error) {
                    console.error("Error loading portal data:", error);
                } finally {
                    setLoading(false);
                }
            } else {
                setUser(null);
                setOrganization(null);
                setLoading(false);
            }
        });
        // x7portal-6e1a3d58 — a tab left open past the session's end notices within 5 minutes.
        const timer = setInterval(async () => {
            const u = auth.currentUser;
            if (!u) return;
            try {
                const { claims } = await u.getIdTokenResult();
                if (portalSessionEnded(claims)) {
                    await signOut(auth);
                    navigate('/portal/login?ended=1', { replace: true });
                }
            } catch (_) { /* offline — try again next time */ }
        }, 5 * 60 * 1000);
        return () => { unsubscribe(); clearInterval(timer); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/portal/login');
    };

    if (loading) return <div className="h-screen flex items-center justify-center">Loading Portal...</div>;

    return (
        <Routes>
            {/* Public Login Route */}
            <Route path="login/:token?" element={<PortalLogin />} />

            {/* Protected Routes (Wrapped in Layout) */}
            <Route element={user ? <PortalLayout user={user} organization={organization} onLogout={handleLogout} /> : <Navigate to="/portal/login" />}>
                <Route index element={<Navigate to="quotes" replace />} />
                
                {/* QUOTES ROUTES */}
                <Route path="quotes" element={<PortalQuotes />} />
                <Route path="quotes/:quoteId" element={<PortalQuoteDetail />} />

                {/* INVOICES ROUTE */}
                <Route path="invoices" element={<PortalInvoices />} />

                {/* OTHER ROUTES */}
                <Route path="appointments" element={<PortalAppointments />} />
                <Route path="appointments/:jobId" element={<PortalAppointmentDetail />} /> {/* PHASE 1.5 VERIFIER: r1-cp-route-3f9a7c12 */}
                <Route path="requests" element={<PortalRequests />} />
                <Route path="requests/new" element={<PortalNewRequest />} />
                <Route path="contact" element={<PortalContact />} />
            </Route>
        </Routes>
    );
}